import React from 'react';
import { ExcelIcon } from '@assets/icons';
import { CloseIcon, DownloadIcon, UploadIcon2, QuestionOutlineIcon } from '@assets/icons';
import { Box, Button, Flex, HStack, Link, Progress, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import ModalTemplate from '@components/modalTemplate';
import { formatFileSize } from '@helpers/utils';
import { useFileUploadPopUp } from '@store/useFileUploadPopUp';
import { isEmpty } from 'lodash';
import { FileUploader } from 'react-drag-drop-files';

export default function UploadFilePopUp() {
    const {
        isOpen,
        onClose,
        title,
        titleDesc,
        files,
        maxFileSize,
        fileTypeAllowed,
        isFileError,
        onUploadFile,
        isUploading,
        isCanCancelUpload,
        onCancelUpload,
        uploadProgress,
        uploadProgressMessage,
        isFinishUploading,
        onDownloadTemplate,
        renderContent,
        uploadButtonText,
        isShowDownloadTemplate,
        helperText,
        set,
        reset,
    } = useFileUploadPopUp();

    const modalWidth = '546px';
    const fileTypeMapping = fileTypeAllowed.map((ext) => ext.toUpperCase()).join(', ');

    const onCloseHandler = () => {
        if (onClose) onClose();
        reset();
    };

    let urlText = {};

    if (helperText.isLink) {
        urlText = {
            as: Link,
            href: helperText.url,
            target: '_blank',
            rel: 'noopener noreferrer',
        };
    }

    return (
        <ModalTemplate isOpen={isOpen} width={modalWidth} onClose={onCloseHandler} isClosebyOverlayClick={false}>
            <Box p={'24px'}>
                <Flex mb={'12px'}>
                    <Text fontWeight={'600'} fontSize={'24px'}>
                        {title}
                    </Text>

                    {helperText.isShow && (
                        <Button variant={helperText.isLink ? 'link' : 'unstyled'} size={'sm'} ml={'auto'} {...urlText}>
                            <QuestionOutlineIcon strokeWidth={3} boxSize={'16px'} />
                            {helperText.text}
                        </Button>
                    )}

                    <Button
                        onClick={onCloseHandler}
                        variant={'unstyled'}
                        disabled={isUploading}
                        display={isUploading ? 'none' : 'flex'}
                        ml={helperText.isShow ? '0' : 'auto'}
                    >
                        <CloseIcon />
                    </Button>
                </Flex>

                {!isFinishUploading ? (
                    <Box>
                        {titleDesc && (
                            <Text mb={'12px'} fontSize={'14px'}>
                                {titleDesc}
                            </Text>
                        )}

                        <Flex alignItems={'center'} justifyContent={'space-between'} mb={'12px'}>
                            <Text fontWeight={'600'}>File di Upload</Text>

                            {isShowDownloadTemplate && (
                                <Button onClick={onDownloadTemplate} variant="text" disabled={isUploading}>
                                    <DownloadIcon size={20} />
                                    <Text color={'sagitarius.500'} mt={'3px'} fontSize={'16px'}>
                                        Download Template
                                    </Text>
                                </Button>
                            )}
                        </Flex>

                        <FileUploader
                            handleChange={(selectedFile) => {
                                set({ isFileError: false, files: [selectedFile] });
                            }}
                            onTypeError={(err) => {
                                const message = `Format file yang diperbolehkan hanya ${fileTypeAllowed
                                    .map((f) => `.${f.toUpperCase()}`)
                                    .join(', ')
                                    .replace(/, ([^,]*)$/, ' atau $1')}.`;

                                set({ files: null, isFileError: message });
                            }}
                            onSizeError={(err) => {
                                set({
                                    files: null,
                                    isFileError: `Ukuran file yang dipilih melebihi batas maksimum ${maxFileSize} MB.`,
                                });
                            }}
                            types={fileTypeAllowed}
                            maxSize={maxFileSize}
                        >
                            {isEmpty(files) ? (
                                <Box>
                                    <Box
                                        border="3px dashed"
                                        borderColor={
                                            !isFileError ? useColorModeValue('gray.300', 'gray.600') : 'danger.200'
                                        }
                                        borderRadius="8px"
                                        p={'40px'}
                                        cursor="pointer"
                                        _hover={{ bg: 'semantic.200' }}
                                        transition="background 0.2s"
                                    >
                                        <VStack spacing={2}>
                                            <UploadIcon2 size={28} color="#C2C2C2" />
                                            <Text fontSize={'16px'} color={'neutral.500'} mb={'16px'} pt={'16px'}>
                                                Drag and drop file disini, atau{' '}
                                                <Box display={'inline'} color={'paragon.500'}>
                                                    Pilih File
                                                </Box>
                                            </Text>
                                            <Text fontSize={'12px'} color={'neutral.400'}>
                                                Format file didukung: {fileTypeMapping} (Maks. {maxFileSize} Mb)
                                            </Text>
                                        </VStack>
                                    </Box>

                                    {isFileError && (
                                        <Text fontSize={'12px'} color={'danger.600'} mt={'12px'}>
                                            {isFileError}
                                        </Text>
                                    )}
                                </Box>
                            ) : (
                                <Flex
                                    bg={'semantic.200'}
                                    textAlign="center"
                                    borderRadius="8px"
                                    p={'12px 16px'}
                                    cursor={!isUploading ? 'pointer' : 'default'}
                                    justifyContent={'space-between'}
                                >
                                    <Box textAlign={'left'}>
                                        <HStack gap={'8px'}>
                                            <ExcelIcon />
                                            <Box>
                                                <Text
                                                    fontSize={'14px'}
                                                    fontWeight={700}
                                                    color={isUploading ? '#7A7A7A' : '#2A3D4A'}
                                                    mb={'4px'}
                                                >
                                                    {files[0].name}
                                                </Text>
                                                <Text mt={'0'} color={'#7A7A7A'} fontSize={'14px'}>
                                                    {formatFileSize(files[0].size)}
                                                </Text>
                                            </Box>
                                        </HStack>
                                    </Box>

                                    <Button variant={'text'} colorScheme="sagitarius" disabled={isUploading}>
                                        <Box fontSize={'14px'} mt={'2.7px'}>
                                            Ganti File
                                        </Box>
                                        <UploadIcon2 size={15} />
                                    </Button>
                                </Flex>
                            )}
                        </FileUploader>

                        {isUploading ? (
                            <Box textAlign="center" mt={'18px'}>
                                {isCanCancelUpload && (
                                    <Button
                                        variant={'outline'}
                                        onClick={onCancelUpload}
                                        p={'22px 52px !important'}
                                        mb={'18px'}
                                    >
                                        Batalkan Upload
                                    </Button>
                                )}

                                <Progress value={uploadProgress} size="sm" mb="12px" isAnimated />
                                <Flex justifyContent="space-between">
                                    <Text>{uploadProgressMessage}</Text>
                                    <Text fontWeight={700}>{uploadProgress}%</Text>
                                </Flex>
                            </Box>
                        ) : (
                            <Flex justifyContent={'center'} mt={'18px'}>
                                <Button onClick={onUploadFile} p={'22px 52px !important'} disabled={isEmpty(files)}>
                                    {uploadButtonText}
                                </Button>
                            </Flex>
                        )}
                    </Box>
                ) : typeof renderContent === 'function' ? (
                    renderContent()
                ) : (
                    renderContent
                )}
            </Box>
        </ModalTemplate>
    );
}
